import React, {useEffect, useState} from 'react';
import Header from '../../components/Header';
import {Footer} from '../../components/Footer';
import {BetsService, TournamentDataSchema, TournamentPredictionOut, UserScoreSchema} from '../../api';
import {Skeleton} from "../../components/Skeleton";
import {Rating} from "../../components/Rating";
import {useAlert} from "../../contexts/AlertContext";

interface Team {
    id: string;
    name: string;
    logo?: string | null;
    group: string;
}

interface StageWithSelection {
    round_name: string;
    selected_team_ids: string[];
}

const MAX_TEAMS_MAP: Record<string, number> = {
    'Групповой этап': 32,
    '1/16 финала': 16,
    '1/8 финала': 8,
    '1/4 финала': 4,
    '1/2 финала': 2,
    'Матч за 3-е место': 1,
    'Финал': 1,
};

const STAGE_TITLES: Record<string, string> = {
    'Групповой этап': 'Выход из группы',
    '1/2 финала': 'Полуфинал',
};

export const Tournament: React.FC = () => {
    const [place, setPlace] = useState<UserScoreSchema>();
    const [stages, setStages] = useState<StageWithSelection[]>([]);
    const [allTeams, setAllTeams] = useState<Team[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const {errorTip} = useAlert();

    useEffect(() => {
        Promise.all([
            BetsService.getUserScore(),
            BetsService.tournamentData(),
        ]).then(([userScoreData, tournamentData]) => {
            setPlace(userScoreData);
            return tournamentData
        }).then((data: TournamentDataSchema) => {
            setAllTeams(data.teams as Team[]);
            return BetsService.tournamentPredictions().then((predictions: TournamentPredictionOut[]) => {
                const merged: StageWithSelection[] = data.stages.map((roundName) => {
                    const pred = predictions.find(p => p.round_name === roundName);
                    return {
                        round_name: roundName,
                        selected_team_ids: pred?.selected_team_ids || [],
                    };
                });
                setStages(merged);
            });
        }).finally(() => setLoading(false));
    }, []);

    const semiFinalIndex = stages.findIndex(s => s.round_name === '1/2 финала');
    const finalIndex = stages.findIndex(s => s.round_name === 'Финал');
    const thirdPlaceIndex = stages.findIndex(s => s.round_name === 'Матч за 3-е место');

    const getAvailableTeams = (stageIndex: number): Team[] => {
        if (!allTeams.length) return [];

        // Первая стадия — все команды
        if (stageIndex === 0) return allTeams;

        // До полуфинала: только выбранные на предыдущей стадии
        if (semiFinalIndex < 0 || stageIndex < semiFinalIndex) {
            const prevSelected = stages[stageIndex - 1]?.selected_team_ids || [];
            return allTeams.filter(t => prevSelected.includes(t.id));
        }

        // Полуфинал: выбранные в 1/4
        if (stageIndex === semiFinalIndex) {
            const prevSelected = stages[stageIndex - 1]?.selected_team_ids || [];
            return allTeams.filter(t => prevSelected.includes(t.id));
        }

        // Финал и 3-е место
        const semiSelected = stages[semiFinalIndex]?.selected_team_ids || [];
        if (semiSelected.length === 0) return [];

        const prevSelected = stages[semiFinalIndex - 1]?.selected_team_ids || [];
        const semiAvailable = allTeams.filter(t => prevSelected.includes(t.id));

        if (stageIndex === finalIndex) return semiAvailable.filter(t => semiSelected.includes(t.id));
        if (stageIndex === thirdPlaceIndex) return semiAvailable.filter(t => !semiSelected.includes(t.id));

        return [];
    };

    const toggleTeam = (stageIndex: number, teamId: string) => {
        const stage = stages[stageIndex];
        const isSelected = stage.selected_team_ids.includes(teamId);
        const max = getMaxTeams(stage.round_name);
        if (!isSelected && stage.selected_team_ids.length >= max) return;

        const newStages = [...stages];
        const updatedStage = { ...stage };

        if (isSelected) {
            updatedStage.selected_team_ids = stage.selected_team_ids.filter(id => id !== teamId);
            for (let i = stageIndex + 1; i < newStages.length; i++) {
                newStages[i] = {
                    ...newStages[i],
                    selected_team_ids: newStages[i].selected_team_ids.filter(id => id !== teamId),
                };
            }
        } else {
            updatedStage.selected_team_ids = [...stage.selected_team_ids, teamId];
        }

        newStages[stageIndex] = updatedStage;

        if (stageIndex === semiFinalIndex) {
            if (finalIndex >= 0) newStages[finalIndex] = { ...newStages[finalIndex], selected_team_ids: [] };
            if (thirdPlaceIndex >= 0) newStages[thirdPlaceIndex] = { ...newStages[thirdPlaceIndex], selected_team_ids: [] };
        }
        if (stageIndex === finalIndex && thirdPlaceIndex >= 0) {
            newStages[thirdPlaceIndex] = {
                ...newStages[thirdPlaceIndex],
                selected_team_ids: newStages[thirdPlaceIndex].selected_team_ids.filter(id => id !== teamId),
            };
        }
        if (stageIndex === thirdPlaceIndex && finalIndex >= 0) {
            newStages[finalIndex] = {
                ...newStages[finalIndex],
                selected_team_ids: newStages[finalIndex].selected_team_ids.filter(id => id !== teamId),
            };
        }

        setStages(newStages);

        BetsService.tournamentToggle({ round_name: stage.round_name, team_id: teamId }).catch((err) => {
            errorTip(err.body?.detail ?? err.toString());
            return Promise.reject(err);
        }).catch(() => {
            BetsService.tournamentPredictions().then((predictions) => {
                const merged = stages.map((s) => {
                    const pred = predictions.find(p => p.round_name === s.round_name);
                    return { ...s, selected_team_ids: pred?.selected_team_ids || [] };
                });
                setStages(merged);
            });
        });
    };

    const getMaxTeams = (roundName: string) => MAX_TEAMS_MAP[roundName] || 99;
    const getStageTitle = (roundName: string) => STAGE_TITLES[roundName] || roundName;

    const groupTeams = (teams: Team[]) => {
        const grouped: Record<string, Team[]> = {};
        for (const t of teams) {
            const g = t.group || '?';
            if (!grouped[g]) grouped[g] = [];
            grouped[g].push(t);
        }
        return grouped;
    };

    if (loading) return <Skeleton />;

    return (
        <>
            <Header />
            <div className='tournament'>
                {place && <Rating place={place}/>}
                <h2 className='tournament__title'>Прогноз на турнир</h2>
                <p className='tournament__subtitle'>
                    Выбери 32 команды из групп, затем 16 в 1/16 финала, и так до чемпиона.
                </p>

                {stages.map((stage, index) => {
                    const available = getAvailableTeams(index);
                    const max = getMaxTeams(stage.round_name);

                    return (
                        <div key={stage.round_name} className='tournament__stage'>
                            <h3 className='tournament__stage-title'>
                                {getStageTitle(stage.round_name)}
                                <span className='tournament__stage-count'>
                                    Выбрано: {stage.selected_team_ids.length} / {max}
                                </span>
                            </h3>

                            {index === 0 ? (
                                <div className='tournament__groups'>
                                    {Object.entries(groupTeams(available)).map(([group, groupTeams]) => (
                                        <div key={group} className='tournament__group'>
                                            <h4 className='tournament__group-title'>{group}</h4>
                                            <div className='tournament__teams'>
                                                {groupTeams.map(team => (
                                                    <button
                                                        key={team.id}
                                                        className={`tournament__team ${stage.selected_team_ids.includes(team.id) ? 'tournament__team--selected' : ''}`}
                                                        onClick={() => toggleTeam(index, team.id)}
                                                        disabled={!stage.selected_team_ids.includes(team.id) && stage.selected_team_ids.length >= max}
                                                    >
                                                        {team.logo && <img src={team.logo} alt={team.name} className='tournament__team-logo' />}
                                                        <span className='tournament__team-name'>{team.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='tournament__teams'>
                                    {available.map(team => (
                                        <button
                                            key={team.id}
                                            className={`tournament__team ${stage.selected_team_ids.includes(team.id) ? 'tournament__team--selected' : ''}`}
                                            onClick={() => toggleTeam(index, team.id)}
                                            disabled={!stage.selected_team_ids.includes(team.id) && stage.selected_team_ids.length >= max}
                                        >
                                            {team.logo && <img src={team.logo} alt={team.name} className='tournament__team-logo' />}
                                            <span className='tournament__team-name'>{team.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {index === semiFinalIndex && (
                                <div className='tournament__branch-arrows'>
                                    <div className='tournament__branch-arrow tournament__branch-arrow--left'>🏆 Выбранные → Финал</div>
                                    <div className='tournament__branch-arrow tournament__branch-arrow--right'>🥉 Остальные → За 3-е место</div>
                                </div>
                            )}
                            {index < stages.length - 1 && index !== semiFinalIndex && (
                                <div className='tournament__arrow'>↓</div>
                            )}
                        </div>
                    );
                })}
            </div>
            <Footer />
        </>
    );
};